import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import LocalizedLink from '@/i18n/LocalizedLink';
import { TERMS_OF_SERVICE_PATH } from '@/data/governedCompliance';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  return (
    <footer className="bg-slate-800 text-white relative overflow-hidden">
      {/* Solid background to prevent any inherited patterns */}
      <div className="absolute inset-0 bg-slate-800" aria-hidden="true"></div>
      
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          
          {/* Company Info - Column 1-2 */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <img
                src="/logo-footer-white.png"
                alt="InternationalVenues.com® by Jigsaw Conferences Ltd"
                className="h-9 w-auto object-contain"
                data-testid="footer-logo-image"
              />
            </div>
            
            <p className="text-slate-300 mb-6 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            
            <a 
              href="https://www.linkedin.com/company/jigsaw-conferences-ltd/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-slate-300 hover:text-blue-400 transition-colors"
            >
              <Linkedin className="w-5 h-5" />
              <span className="text-sm">{t('footer.followLinkedIn')}</span>
            </a>
          </div>

          {/* Platform Features */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.platformFeatures')}</h3>
            <ul className="text-sm text-slate-300 space-y-2">
              <li>
                <LocalizedLink to="/#trending" className="hover:text-blue-400 transition-colors">
                  {t('footer.trendingEvents')}
                </LocalizedLink>
              </li>
              <li>
                <Link to="/organiser/events" className="hover:text-blue-400 transition-colors">
                  {t('footer.aiWriting')}
                </Link>
              </li>
              <li>
                <LocalizedLink to="/events" className="hover:text-blue-400 transition-colors">
                  {t('footer.recommendations')}
                </LocalizedLink>
              </li>
              <li>
                <Link to="/admin/partner-keys" className="hover:text-blue-400 transition-colors">
                  {t('footer.partnerApi')}
                </Link>
              </li>
              <li>
                <Link to="/admin/trending" className="hover:text-blue-400 transition-colors">
                  {t('footer.predictiveAnalytics')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Tools & Resources + Organisers */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.toolsResources')}</h3>
            <ul className="text-sm text-slate-300 space-y-2">
              <li>
                <LocalizedLink to="/blog" className="hover:text-blue-400 transition-colors">
                  {t('footer.blog')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/insights" className="hover:text-blue-400 transition-colors">
                  {t('footer.insightsReports')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/boost/dashboard" className="hover:text-orange-400 transition-colors flex items-center gap-1">
                  <span>{t('footer.eventBoost')}</span>
                  <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded">NEW</span>
                </LocalizedLink>
              </li>
              <li>
                <a href="/api/public/sitemap.xml" className="hover:text-blue-400 transition-colors">
                  {t('footer.sitemap')}
                </a>
              </li>
            </ul>
            
            <h3 className="text-lg font-semibold mb-4 mt-8 text-white">{t('footer.organisers')}</h3>
            <ul className="text-sm text-slate-300 space-y-2">
              <li>
                <LocalizedLink to="/events/list-your-event" className="hover:text-blue-400 transition-colors">
                  {t('footer.listYourEvent')}
                </LocalizedLink>
              </li>
              <li>
                <Link to="/organiser/events" className="hover:text-blue-400 transition-colors">
                  {t('footer.manageEvent')}
                </Link>
              </li>
              <li>
                <LocalizedLink to="/events/list-your-event" className="hover:text-blue-400 transition-colors">
                  {t('footer.submitEdition')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/events/list-your-event" className="hover:text-blue-400 transition-colors">
                  {t('footer.requestBadge')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Developers & Partners */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">{t('footer.developersPartners')}</h3>
            <ul className="text-sm text-slate-300 space-y-2">
              <li>
                <a href="/api/docs" className="hover:text-blue-400 transition-colors">
                  {t('footer.apiDocs')}
                </a>
              </li>
              <li>
                <Link to="/admin/partner-keys" className="hover:text-blue-400 transition-colors">
                  {t('footer.embedWidgets')}
                </Link>
              </li>
              <li>
                <a href="mailto:api@jigsawconferences.co.uk" className="hover:text-blue-400 transition-colors">
                  {t('footer.integrationSupport')}
                </a>
              </li>
              <li>
                <Link to="/admin/login" className="hover:text-blue-400 transition-colors">
                  {t('footer.partnerLogin')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation Links Row */}
      <div className="border-t border-slate-700 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-slate-300">
            <LocalizedLink to="/blog" className="hover:text-blue-400 transition-colors">{t('footer.newsArticles')}</LocalizedLink>
            <span className="text-slate-600">•</span>
            <a href="https://jigsawconferences.co.uk/about" className="hover:text-blue-400 transition-colors">{t('footer.aboutUs')}</a>
            <span className="text-slate-600">•</span>
            <a href="https://jigsawconferences.co.uk/contact" className="hover:text-blue-400 transition-colors">{t('footer.contactUs')}</a>
            <span className="text-slate-600">•</span>
            <a href="https://jigsawconferences.co.uk/enterprise" className="hover:text-blue-400 transition-colors">{t('footer.enterprise')}</a>
          </div>
        </div>
      </div>

      {/* Legal Links Row */}
      <div className="border-t border-slate-700 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-sm text-slate-400">
            <span>{t('footer.copyright', { year: currentYear })}</span>
            <span className="text-slate-600">•</span>
            <LocalizedLink to="/privacy" className="hover:text-blue-400 transition-colors">{t('footer.privacy')}</LocalizedLink>
            <span className="text-slate-600">•</span>
            <LocalizedLink to="/cookies" className="hover:text-blue-400 transition-colors">{t('footer.cookies')}</LocalizedLink>
            <span className="text-slate-600">•</span>
            <a href={TERMS_OF_SERVICE_PATH} className="hover:text-blue-400 transition-colors" data-testid="footer-terms-link">{t('footer.terms')}</a>
            <span className="text-slate-600">•</span>
            <LocalizedLink to="/compliance" className="hover:text-blue-400 transition-colors" data-testid="footer-compliance-page-link">Corporate certifications and accreditations</LocalizedLink>
            <span className="text-slate-600">•</span>
            <LocalizedLink to="/about-our-listings" className="hover:text-blue-400 transition-colors" data-testid="footer-about-our-listings-link">{t('footer.aboutListings')}</LocalizedLink>
            <span className="text-slate-600">•</span>
            <a href="https://jigsawconferences.co.uk/modern-slavery" className="hover:text-blue-400 transition-colors">{t('footer.modernSlavery')}</a>
            <span className="text-slate-600">•</span>
            <a href="https://jigsawconferences.co.uk/safeguarding" className="hover:text-blue-400 transition-colors">{t('footer.safeguarding')}</a>
            <span className="text-slate-600">•</span>
            <a href="/api/public/sitemap.xml" className="hover:text-blue-400 transition-colors">{t('footer.sitemap')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
